import { installMobileModal } from './modal';
import { isCanvasEscape } from '../workspace/canvas-focus';

export type SheetSnap = 0 | 0.5 | 1;
type Position = { x: number; y: number; time: number };

/** One detent per deliberate swipe; a fast short swipe has the same intent. */
export function sheetReleaseSnap(
  origin: Exclude<SheetSnap, 0>,
  dy: number,
  velocity: number,
  height: number,
): SheetSnap {
  if (Math.abs(dy) < height * 0.25 && Math.abs(velocity) < 0.5) return origin;
  const direction = Math.abs(velocity) >= 0.5 ? velocity : dy;
  return direction > 0 ? (origin === 1 ? 0.5 : 0) : 1;
}

export type CanvasSheetOptions = {
  sheet: HTMLElement;
  scrim: HTMLElement;
  handle: HTMLElement;
  initialSnap: Exclude<SheetSnap, 0>;
  onClose: () => unknown;
  full?: boolean;
  onBack?: () => unknown;
};

/** What the canvas covers leaves the Tab order until it is uncovered. */
function coverBackground(covered: HTMLElement[]): () => void {
  const previous = covered.map((element) => ({ element, inert: element.inert }));
  for (const { element } of previous) element.inert = true;
  return () => {
    for (const { element, inert } of previous) element.inert = inert;
  };
}

/** One modality owner for overlays; snap geometry stays phone-only. */
export function installCanvasSheet({
  sheet,
  scrim,
  handle,
  initialSnap,
  onClose,
  full = false,
  onBack = onClose,
}: CanvasSheetOptions): () => void {
  const document = sheet.ownerDocument;
  const window = document.defaultView!;
  const narrow = window.matchMedia?.('(max-width: 640px)') ?? {
    matches: false,
    addEventListener() {},
    removeEventListener() {},
  };
  let modal = false;
  const header = sheet.querySelector<HTMLElement>('.ws-canvas-top');
  let snap = initialSnap;
  let start: Position | null = null;
  let last: Position | null = null;
  let pointer: number | null = null;
  let dragging = false;
  let originHeight = 0;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  let modalCleanup: ((returnFocus?: boolean) => void) | undefined;
  const originalRole = sheet.getAttribute('role');
  const originalModal = sheet.getAttribute('aria-modal');

  const position = (event: PointerEvent): Position => ({
    x: event.clientX,
    y: event.clientY,
    time: event.timeStamp,
  });
  const reset = () => {
    const held = pointer;
    pointer = null;
    start = last = null;
    dragging = false;
    sheet.removeAttribute('data-sheet-dragging');
    sheet.style.removeProperty('--mobile-sheet-drag-height');
    if (held !== null && sheet.hasPointerCapture?.(held)) sheet.releasePointerCapture(held);
  };
  const render = () => {
    sheet.dataset.sheetSnap = String(snap);
    handle.setAttribute('aria-valuenow', String(snap * 100));
  };
  const settle = (next: SheetSnap) => {
    clearTimeout(settleTimer);
    sheet.toggleAttribute('data-sheet-settling', next !== 0);
    settleTimer = setTimeout(() => sheet.removeAttribute('data-sheet-settling'), 250);
    reset();
    if (next === 0) {
      // Leave a visible sheet if the host refuses to close dirty content.
      render();
      onClose();
    } else {
      snap = next;
      render();
    }
  };
  const down = (event: PointerEvent) => {
    if (!narrow.matches) return;
    if (!event.isPrimary || pointer !== null) {
      reset();
      return;
    }
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (
      !handle.contains(target) &&
      (!header?.contains(target) ||
        target.closest('button, a, input, select, textarea, [contenteditable="true"]'))
    )
      return;
    start = last = position(event);
    pointer = event.pointerId;
    originHeight = sheet.getBoundingClientRect().height;
  };
  const move = (event: PointerEvent) => {
    if (event.pointerId !== pointer || !start) return;
    const dy = event.clientY - start.y;
    const dx = event.clientX - start.x;
    if (!dragging) {
      if (Math.hypot(dx, dy) < 8) return;
      if (Math.abs(dx) > Math.abs(dy) * 0.75) {
        reset();
        return;
      }
      dragging = true;
      sheet.removeAttribute('data-sheet-settling');
      sheet.setPointerCapture(event.pointerId);
      sheet.setAttribute('data-sheet-dragging', '');
    }
    event.preventDefault();
    const maxHeight = parseFloat(window.getComputedStyle(sheet).maxHeight) || window.innerHeight;
    sheet.style.setProperty(
      '--mobile-sheet-drag-height',
      `${Math.max(0, Math.min(maxHeight, originHeight - dy))}px`,
    );
    last = position(event);
  };
  const up = (event: PointerEvent) => {
    if (event.pointerId !== pointer || !start || !last) return;
    if (!dragging) {
      reset();
      return;
    }
    const dy = last.y - start.y;
    const velocity = dy / Math.max(1, last.time - start.time);
    // A handle drag must not also activate its click-to-resize behavior.
    suppressClick = true;
    window.setTimeout(() => {
      suppressClick = false;
    }, 0);
    settle(sheetReleaseSnap(snap, dy, velocity, originHeight));
  };
  let suppressClick = false;
  const click = (event: MouseEvent) => {
    if (suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (narrow.matches && handle.contains(event.target as Node)) settle(snap === 1 ? 0.5 : 1);
  };
  const cancel = (event: PointerEvent) => {
    if (event.pointerId === pointer) reset();
  };
  // Touch starts with implicit capture on the handle. Its bubbled lost event
  // belongs to the transfer to the sheet, not cancellation of that gesture.
  const lostCapture = (event: PointerEvent) => {
    if (event.target === sheet) cancel(event);
  };
  const key = (event: KeyboardEvent) => {
    if (event.defaultPrevented) return;
    if (narrow.matches && handle.contains(event.target as Node)) {
      if (event.key === 'ArrowUp' || event.key === 'End') {
        event.preventDefault();
        settle(1);
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        settle(snap === 1 ? 0.5 : 0);
      }
      if (event.key === 'Home') {
        event.preventDefault();
        settle(0.5);
      }
    }
    if (modal && isCanvasEscape(event, sheet)) {
      event.preventDefault();
      onBack();
    }
  };
  const close = () => settle(0);
  const viewportChanged = () => {
    sheet.removeAttribute('data-sheet-settling');
    reset();
  };
  const restoreAttribute = (name: string, value: string | null) =>
    value === null ? sheet.removeAttribute(name) : sheet.setAttribute(name, value);
  const updateMode = () => {
    reset();
    modalCleanup?.(false);
    modalCleanup = undefined;
    scrim.hidden = !narrow.matches;
    sheet.toggleAttribute('data-mobile-sheet', narrow.matches);
    if (narrow.matches) render();
    else sheet.removeAttribute('data-sheet-snap');
    // A phone sheet or full screen is all there is: a modal. On a tablet the canvas covers only the work column, so
    // that column leaves the Tab order and the sidebar stays live navigation, never visible but dead.
    modal = narrow.matches || full;
    const covering = !modal && getComputedStyle(sheet).position === 'fixed';
    const shell = sheet.closest('.ws-shell');
    const background = (selector: string) =>
      shell ? Array.from(shell.querySelectorAll<HTMLElement>(selector)) : [];
    if (modal) {
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      modalCleanup = installMobileModal(
        sheet,
        background(':scope > .ws-main-column, :scope > .ws-sidebar'),
      );
    } else {
      restoreAttribute('role', originalRole);
      restoreAttribute('aria-modal', originalModal);
      sheet.removeAttribute('data-sheet-snap');
      if (covering) modalCleanup = coverBackground(background(':scope > .ws-main-column'));
    }
  };
  updateMode();
  narrow.addEventListener('change', updateMode);
  window.visualViewport?.addEventListener('resize', viewportChanged);
  window.addEventListener('resize', updateMode);
  document.addEventListener('pointerdown', down);
  sheet.addEventListener('pointermove', move);
  sheet.addEventListener('pointerup', up);
  sheet.addEventListener('pointercancel', cancel);
  sheet.addEventListener('lostpointercapture', lostCapture);
  sheet.addEventListener('keydown', key);
  handle.addEventListener('click', click);
  scrim.addEventListener('click', close);
  return () => {
    reset();
    modalCleanup?.();
    clearTimeout(settleTimer);
    window.visualViewport?.removeEventListener('resize', viewportChanged);
    window.removeEventListener('resize', updateMode);
    narrow.removeEventListener('change', updateMode);
    document.removeEventListener('pointerdown', down);
    sheet.removeEventListener('pointermove', move);
    sheet.removeEventListener('pointerup', up);
    sheet.removeEventListener('pointercancel', cancel);
    sheet.removeEventListener('lostpointercapture', lostCapture);
    sheet.removeEventListener('keydown', key);
    handle.removeEventListener('click', click);
    scrim.removeEventListener('click', close);
    sheet.removeAttribute('data-mobile-sheet');
    sheet.removeAttribute('data-sheet-snap');
    sheet.removeAttribute('data-sheet-settling');
    restoreAttribute('role', originalRole);
    restoreAttribute('aria-modal', originalModal);
  };
}
